import Debug from 'debug'
import {
  autorun,
  isObservable,
  makeAutoObservable,
  observable,
  set,
} from 'mobx'
import type { RefObject } from 'react'
import { useState } from 'react'
import { useIsomorphicLayoutEffect } from 'react-use'
import { createObservableRef } from './useObservableElementRef'
const debug = Debug('jotta:hooks:useElementSizeObservable')

export function createResizeObservable<T extends HTMLElement>(
  refParam?: RefObject<T>,
  trackScrolling = false,
  initialRefValue: T | null = null,
) {
  const ref = refParam || createObservableRef<T>(initialRefValue)
  if (!isObservable(ref)) {
    throw new Error('ref must be a Mobx observable')
  }
  const rect = observable<Omit<DOMRectReadOnly, 'toJSON'>>({
    bottom: 0,
    height: 0,
    left: 0,
    right: 0,
    top: 0,
    width: 0,
    x: 0,
    y: 0,
  })
  const scroll = observable({
    top: 0,
    scrollHeight: 0,
  })
  return makeAutoObservable(
    {
      ref,
      get hasDimensions() {
        return Boolean(rect.width && rect.height)
      },
      get scroll(): typeof scroll {
        return scroll
      },
      set scroll(value: Partial<typeof scroll>) {
        set(scroll, value)
      },
      get rect(): Omit<DOMRectReadOnly, 'toJSON'> {
        return rect
      },
      set rect(value: Partial<typeof rect>) {
        set(rect, value)
      },
      onResize(entries: ResizeObserverEntry[]) {
        const el = ref.current
        if (el) {
          for (const entry of entries) {
            if (entry.target === el) {
              set(this.rect, entry.contentRect.toJSON())
            }
          }
        }
      },
      onScroll(e: Event) {
        if (e.target instanceof HTMLElement) {
          this.scroll = {
            top: e.target.scrollTop,
            scrollHeight: e.target.scrollHeight,
          }
        }
      },
    },
    {},
    { autoBind: true },
  )
}
export function useElementSizeObservable<
  T extends HTMLElement = HTMLDivElement,
>({
  refParam,
  initialRefValue = null,
  trackScrolling = false,
}: {
  refParam?: RefObject<T>
  initialRefValue?: T | null
  trackScrolling?: boolean
} = {}) {
  const ref = refParam || createObservableRef(initialRefValue)
  if (!isObservable(ref)) {
    throw new Error('ref must be a Mobx observable')
  }
  const [store] = useState(createResizeObservable(ref, trackScrolling))
  useIsomorphicLayoutEffect(
    () =>
      autorun(() => {
        const el = ref.current
        debug('Reaction')
        if (el) {
          debug('Start observing')
          const observer = new ResizeObserver(store.onResize)
          observer.observe(el)
          if (trackScrolling) {
            el.addEventListener('scroll', store.onScroll)
          }
          return () => {
            debug('Disconnect')
            el.removeEventListener('scroll', store.onScroll)
            observer.unobserve(el)
            observer.disconnect()
          }
        }
      }),
    [],
  )
  return store
}
